import { v4 } from 'uuid';
import { SportType } from '../../../models/court/dto/court.dto';
import {
    DEFAULT_SPORT_PRICING,
    FALLBACK_SPORT_PRICING,
    PROVISION_COMMITMENT_MONTHS,
    ProvisionAdminAccessMode,
    ProvisionOrganizationInputDto,
    PROVISION_SLOT_DURATIONS_MINUTES,
} from '../../../models/organization-provisioning/dto/provision-organization.dto';

/**
 * Form state for the superadmin "pre-provision a club" page, and the single
 * place that converts it to the API payload. Kept pure so the unit conversions
 * — the part that can silently be wrong — are tested without rendering.
 *
 * Units, matching the API DTO:
 *   - court prices and coach rates stay MAJOR units (euros);
 *   - membership prices are typed in euros here and sent as CENTS.
 */

export type CourtRow = { key: string; name: string; sport: SportType; isOutdoor: boolean };
export type SportPricingRow = {
    sport: SportType;
    slotDurationMinutes: number;
    offPeakPrice: string;
    peakPrice: string;
};
export type CoachRow = { key: string; firstName: string; lastName: string; hourlyRate: string; phone: string };
export type MembershipRow = {
    key: string;
    name: string;
    monthlyPrice: string;
    commitmentMonths: number;
    courtBookingDiscountPercent: string;
};

export type ProvisioningForm = {
    name: string;
    adminEmail: string;
    adminAccessMode: ProvisionAdminAccessMode;
    city: string;
    address: string;
    postalCode: string;
    courts: CourtRow[];
    pricing: SportPricingRow[];
    openingTime: string;
    closingTime: string;
    weekdayPeakStartTime: string;
    weekendAllDayPeak: boolean;
    coaches: CoachRow[];
    memberships: MembershipRow[];
};

export const SLOT_DURATION_OPTIONS = PROVISION_SLOT_DURATIONS_MINUTES as readonly number[];
export const COMMITMENT_OPTIONS = PROVISION_COMMITMENT_MONTHS as readonly number[];

export const newCourtRow = (index: number, sport: SportType = SportType.PADEL): CourtRow => ({
    key: v4(),
    name: `${sport} ${index}`,
    sport,
    isOutdoor: false,
});
export const newCoachRow = (): CoachRow => ({ key: v4(), firstName: '', lastName: '', hourlyRate: '50', phone: '' });
export const newMembershipRow = (): MembershipRow => ({
    key: v4(),
    name: '',
    monthlyPrice: '',
    commitmentMonths: 1,
    courtBookingDiscountPercent: '0',
});

/** The API's own starter grid (synced DTO), as editable form strings. */
export function defaultPricingFor(sport: SportType): SportPricingRow {
    const pricing = DEFAULT_SPORT_PRICING[sport] ?? FALLBACK_SPORT_PRICING;
    return {
        sport,
        slotDurationMinutes: pricing.slotDurationMinutes,
        offPeakPrice: String(pricing.offPeakPrice),
        peakPrice: String(pricing.peakPrice),
    };
}

export function createInitialForm(): ProvisioningForm {
    return {
        name: '',
        adminEmail: '',
        // Force-add by default: the manager is admin immediately, no email is
        // sent, and the one-time login link is handed over on the call.
        adminAccessMode: ProvisionAdminAccessMode.ADD,
        city: '',
        address: '',
        postalCode: '',
        courts: [newCourtRow(1), newCourtRow(2)],
        pricing: [defaultPricingFor(SportType.PADEL)],
        openingTime: '08:00',
        closingTime: '23:00',
        weekdayPeakStartTime: '17:00',
        weekendAllDayPeak: true,
        coaches: [],
        memberships: [
            {
                key: v4(),
                name: 'Premium',
                monthlyPrice: '49',
                commitmentMonths: 1,
                courtBookingDiscountPercent: '10',
            },
        ],
    };
}

/**
 * Keep one pricing row per sport the courts actually use, preserving edits,
 * adding the starter grid for a newly used sport and dropping unused ones.
 */
export function syncPricingWithCourts(
    pricing: SportPricingRow[],
    courts: CourtRow[],
): SportPricingRow[] {
    const sports = [...new Set(courts.map((c) => c.sport))];
    return sports.map((sport) => pricing.find((p) => p.sport === sport) ?? defaultPricingFor(sport));
}

/** "08:30" → 510. "24:00" → 1440 (open until midnight). Throws on anything else. */
export function timeToMinuteOfDay(value: string): number {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
    if (!match) throw new Error(`Invalid time "${value}"`);
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (minutes > 59 || hours > 24 || (hours === 24 && minutes !== 0)) {
        throw new Error(`Invalid time "${value}"`);
    }
    return hours * 60 + minutes;
}

/** Parses "12,5" or "12.5". Throws on an empty or non-numeric value. */
export function parseAmount(value: string, field: string): number {
    const normalized = value.trim().replace(',', '.');
    const amount = Number(normalized);
    if (normalized === '' || !Number.isFinite(amount)) {
        throw new Error(`${field}: "${value}" is not a number`);
    }
    return amount;
}

/** Euros typed in the form → integer cents, rounded so 49.99 is 4999, not 4998.999… */
export function eurosToCents(value: string, field: string): number {
    return Math.round(parseAmount(value, field) * 100);
}

const optional = (value: string) => {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
};

/**
 * Form → `POST organization-provisioning` body. Always sends `courts`,
 * `coaches` and `memberships` explicitly — an empty list means "none", which
 * is what an operator who removed every row expects (the API would otherwise
 * apply its starter template for an omitted field).
 */
export function toProvisionPayload(form: ProvisioningForm): ProvisionOrganizationInputDto {
    const location = {
        city: optional(form.city),
        address: optional(form.address),
        postalCode: optional(form.postalCode),
    };
    return {
        name: form.name.trim(),
        adminEmail: optional(form.adminEmail),
        adminAccessMode: optional(form.adminEmail) ? form.adminAccessMode : undefined,
        location,
        courts: form.courts.map((c) => ({ name: c.name.trim(), sport: c.sport, isOutdoor: c.isOutdoor })),
        pricing: syncPricingWithCourts(form.pricing, form.courts).map((p) => ({
            sport: p.sport,
            slotDurationMinutes: p.slotDurationMinutes,
            offPeakPrice: parseAmount(p.offPeakPrice, `${p.sport} off-peak price`),
            peakPrice: parseAmount(p.peakPrice, `${p.sport} peak price`),
        })),
        schedule: {
            openingMinuteOfDay: timeToMinuteOfDay(form.openingTime),
            closingMinuteOfDay: timeToMinuteOfDay(form.closingTime),
            weekdayPeakStartMinuteOfDay: optional(form.weekdayPeakStartTime)
                ? timeToMinuteOfDay(form.weekdayPeakStartTime)
                : null,
            weekendAllDayPeak: form.weekendAllDayPeak,
        },
        coaches: form.coaches.map((c) => ({
            firstName: c.firstName.trim(),
            lastName: c.lastName.trim(),
            hourlyRate: parseAmount(c.hourlyRate, `${c.firstName} ${c.lastName} hourly rate`),
            phone: optional(c.phone),
        })),
        memberships: form.memberships.map((m) => ({
            name: m.name.trim(),
            priceInCents: eurosToCents(m.monthlyPrice, `${m.name} price`),
            commitmentMonths: m.commitmentMonths,
            courtBookingDiscountPercent: parseAmount(m.courtBookingDiscountPercent, `${m.name} discount`),
        })),
    };
}
